import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, Users, Flame, Target, Award, BookOpen, 
  DollarSign, Activity, BarChart3, RefreshCw, Download,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

const API_URL = getApiUrl();

// Simple bar chart component
const SimpleBarChart = ({ data, labelKey, valueKey, color = '#00E5CC', maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d[valueKey]));
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={item[labelKey] ?? `bar-${i}`} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-20 truncate">{item[labelKey]}</span>
          <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(item[valueKey] / max * 100) || 0}%`,
                backgroundColor: color
              }}
            />
          </div>
          <span className="text-xs font-medium w-12 text-right">{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
};

// Engagement Analytics Card
export const EngagementAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/engagement');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch engagement data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="engagement-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#00E5CC]" />
          User Engagement
        </CardTitle>
        <CardDescription>Active users and engagement trends</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 dark:text-green-400">{data.daily_active_users}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Daily Active</div>
          </div>
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{data.weekly_active_users}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Weekly Active</div>
          </div>
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 dark:text-purple-400">{data.monthly_active_users}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Monthly Active</div>
          </div>
        </div>
        
        <div className="mt-4">
          <h4 className="text-sm font-medium mb-2">Daily Active Users (14 days)</h4>
          <SimpleBarChart 
            data={data.daily_engagement?.slice(-7) || []} 
            labelKey="date" 
            valueKey="active_users"
            color="#10B981"
          />
        </div>
      </CardContent>
    </Card>
  );
};

// Streak Analytics Card
export const StreakAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/streaks');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch streak data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="streak-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-500" />
          Streak Analytics
        </CardTitle>
        <CardDescription>User streak distribution and milestones</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{data.avg_current_streak}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Avg Current</div>
          </div>
          <div className="text-center p-3 bg-red-50 dark:bg-red-950/30 rounded-lg">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{data.avg_longest_streak}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Avg Longest</div>
          </div>
          <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{data.max_streak}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Max Streak</div>
          </div>
        </div>

        <h4 className="text-sm font-medium mb-2">Streak Distribution</h4>
        <SimpleBarChart 
          data={data.distribution || []} 
          labelKey="range" 
          valueKey="count"
          color="#F97316"
        />

        <h4 className="text-sm font-medium mt-4 mb-2">Milestone Achievements</h4>
        <div className="grid grid-cols-3 gap-2 text-xs">
          {Object.entries(data.milestone_counts || {}).map(([key, count]) => (
            <div key={key} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
              <span>{key.replace('_', ' ')}</span>
              <Badge variant="secondary">{count}</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Challenge Analytics Card
export const ChallengeAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/challenges');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch challenge data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="challenge-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Target className="h-5 w-5 text-purple-500" />
          Challenge Completion
        </CardTitle>
        <CardDescription>Challenge participation and completion rates</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{data.daily?.rate || 0}%</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Daily Rate</div>
            <div className="text-xs text-muted-foreground">{data.daily?.completed || 0}/{data.daily?.total || 0}</div>
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
            <div className="text-lg font-bold text-green-600 dark:text-green-400 dark:text-green-400">{data.weekly?.rate || 0}%</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Weekly Rate</div>
            <div className="text-xs text-muted-foreground">{data.weekly?.completed || 0}/{data.weekly?.total || 0}</div>
          </div>
          <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="text-lg font-bold text-purple-600 dark:text-purple-400 dark:text-purple-400">{data.monthly?.rate || 0}%</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Monthly Rate</div>
            <div className="text-xs text-muted-foreground">{data.monthly?.completed || 0}/{data.monthly?.total || 0}</div>
          </div>
          <div className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
            <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{data.friend_challenges?.completed || 0}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Friend Challenges</div>
            <div className="text-xs text-muted-foreground">{data.friend_challenges?.total || 0} total</div>
          </div>
        </div>

        {data.top_completers?.length > 0 && (
          <>
            <h4 className="text-sm font-medium mb-2">Top Completers</h4>
            <div className="space-y-1">
              {data.top_completers.slice(0, 5).map((user, i) => (
                <div key={user.user_id || user.email || `completer-${i}`} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="truncate">{user.name || user.email}</span>
                  <Badge>{user.completions} completed</Badge>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// XP Leaderboard Card
export const XPAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/xp');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch XP data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="xp-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#00E5CC]" />
          XP & Leveling
        </CardTitle>
        <CardDescription>Experience points distribution and top earners</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-teal-50 dark:bg-teal-950/30 rounded-lg">
            <div className="text-lg font-bold text-teal-600 dark:text-teal-400">{data.total_xp_earned?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Total XP Earned</div>
          </div>
          <div className="p-3 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg">
            <div className="text-lg font-bold text-cyan-600 dark:text-cyan-400">{Math.round(data.avg_xp)?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Avg XP/User</div>
          </div>
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{data.avg_level}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Avg Level</div>
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
            <div className="text-lg font-bold text-green-600 dark:text-green-400 dark:text-green-400">{data.max_xp?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Top XP</div>
          </div>
        </div>

        <h4 className="text-sm font-medium mb-2">XP Distribution</h4>
        <SimpleBarChart 
          data={data.distribution || []} 
          labelKey="range" 
          valueKey="count"
          color="#14B8A6"
        />

        {data.top_earners?.length > 0 && (
          <>
            <h4 className="text-sm font-medium mt-4 mb-2">Top XP Earners</h4>
            <div className="space-y-1">
              {data.top_earners.slice(0, 5).map((user, i) => (
                <div key={user.user_id || user.email || `earner-${i}`} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <div>
                    <span className="font-medium">{user.name || user.email}</span>
                    <Badge variant="outline" className="ml-2">Lv {user.level || 1}</Badge>
                  </div>
                  <span className="font-bold text-teal-600 dark:text-teal-400">{user.total_xp?.toLocaleString()} XP</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Phrase Analytics Card
export const PhraseAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/phrases');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch phrase data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="phrase-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-blue-500" />
          Phrase Analytics
        </CardTitle>
        <CardDescription>Content views and popular categories</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{data.total_views?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Total Views</div>
          </div>
          <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{data.total_phrases?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Total Phrases</div>
          </div>
          <div className="text-center p-3 bg-violet-50 dark:bg-violet-950/30 rounded-lg">
            <div className="text-2xl font-bold text-violet-600 dark:text-violet-400">{data.avg_views_per_user}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Avg Views/User</div>
          </div>
        </div>

        {data.top_viewed_phrases?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Top Viewed Phrases</h4>
            <div className="space-y-1">
              {data.top_viewed_phrases.map((p, i) => (
                <div key={p.phrase_id || p.phrase || `viewed-${i}`} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="truncate flex-1">{p.phrase}</span>
                  <Badge variant="secondary" className="ml-2">{p.view_count} views</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.top_favorited_phrases?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Top Favorited Phrases</h4>
            <div className="space-y-1">
              {data.top_favorited_phrases.map((p, i) => (
                <div key={p.phrase_id || p.phrase || `fav-${i}`} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="truncate flex-1">{p.phrase}</span>
                  <Badge variant="secondary" className="ml-2">{p.favorite_count} favs</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <h4 className="text-sm font-medium mb-2">Top Categories by Phrase Count</h4>
        <SimpleBarChart 
          data={data.top_categories || []} 
          labelKey="name" 
          valueKey="phrase_count"
          color="#6366F1"
        />
      </CardContent>
    </Card>
  );
};

// Badge Analytics Card
export const BadgeAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/badges');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch badge data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="badge-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Award className="h-5 w-5 text-yellow-500" />
          Badge Unlock Stats
        </CardTitle>
        <CardDescription>Badge distribution across {data.total_users} users</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {data.badge_stats?.map((badge, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-28 truncate">{badge.name}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                <div 
                  className="h-full rounded-full bg-yellow-400 transition-all duration-500"
                  style={{ width: `${badge.percentage}%` }}
                />
              </div>
              <span className="text-xs font-medium w-20 text-right">
                {badge.unlocked_count} ({badge.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Subscription Analytics Card
export const SubscriptionAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/subscriptions');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch subscription data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="subscription-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-500" />
          Subscriptions & Revenue
        </CardTitle>
        <CardDescription>Subscription breakdown and estimated revenue</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
            <div className="text-xl font-bold text-green-600 dark:text-green-400 dark:text-green-400">${data.estimated_mrr}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Est. MRR</div>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
            <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{data.paid_count}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Paid Subs</div>
          </div>
          <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="text-xl font-bold text-purple-600 dark:text-purple-400 dark:text-purple-400">{data.gifted_count}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Gifted</div>
          </div>
          <div className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
            <div className="text-xl font-bold text-orange-600 dark:text-orange-400">{data.conversion_rate}%</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Trial Conv.</div>
          </div>
        </div>

        <h4 className="text-sm font-medium mb-2">Status Breakdown</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {Object.entries(data.status_breakdown || {}).map(([status, count]) => (
            <div key={status} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
              <span className="capitalize">{status || 'Unknown'}</span>
              <Badge variant="secondary">{count}</Badge>
            </div>
          ))}
        </div>

        <h4 className="text-sm font-medium mt-4 mb-2">Tier Breakdown</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {Object.entries(data.tier_breakdown || {}).map(([tier, count]) => (
            <div key={tier} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
              <span className="capitalize">{tier}</span>
              <Badge variant="secondary">{count}</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Recent Activity Card
export const RecentActivity = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/activity/recent');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch activity data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  return (
    <Card data-testid="recent-activity-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="h-5 w-5 text-indigo-500" />
          Recent Activity
        </CardTitle>
        <CardDescription>Latest user logins and signups</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium mb-2">Recent Logins</h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {data.recent_logins?.slice(0, 5).map((user, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="truncate max-w-[120px]">{user.name || user.email}</span>
                  <span className="text-muted-foreground">{formatDate(user.last_login)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-2">Recent Signups</h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {data.recent_signups?.slice(0, 5).map((user, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-green-50 dark:bg-green-950/30 p-2 rounded">
                  <span className="truncate max-w-[120px]">{user.name || user.email}</span>
                  <span className="text-muted-foreground">{formatDate(user.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Quick Actions Panel
export const QuickActionsPanel = ({ onExportUsers }) => {
  const [sending, setSending] = useState(false);

  const handleExport = async () => {
    if (onExportUsers) {
      onExportUsers();
    } else {
      try {
        const { data: users, error } = await apiFetch('/api/admin/users?limit=1000');
        if (!error && users) {
          const csv = [
            ['Email', 'Name', 'Status', 'Streak', 'Phrases Viewed', 'Created'],
            ...users.map(u => [
              u.email, u.name || '', u.subscription_status || 'free',
              u.current_streak || 0, u.phrases_viewed || 0, u.created_at || ''
            ])
          ].map(row => row.join(',')).join('\n');
          
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          toast.success('Users exported successfully');
        }
      } catch (e) {
        toast.error('Failed to export users');
      }
    }
  };

  return (
    <Card data-testid="quick-actions-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#00E5CC]" />
          Quick Actions
        </CardTitle>
        <CardDescription>Common admin tasks</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={handleExport}
          >
            <Download className="h-4 w-4 mr-1" />
            Export Users
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh Data
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Guest Analytics Card
export const GuestAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/guest/stats');
      if (!error && result) {
        setData(result);
      }
    } catch (e) {
      console.error('Failed to fetch guest stats:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading guest data...</p></CardContent></Card>;
  if (!data || data.error) return null;

  return (
    <Card data-testid="guest-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5 text-purple-500" />
          Guest Visitors
        </CardTitle>
        <CardDescription>Anonymous visitor tracking</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{data.today?.visits || 0}</div>
            <div className="text-xs text-muted-foreground">Visits Today</div>
          </div>
          <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{data.today?.unique_guests || 0}</div>
            <div className="text-xs text-muted-foreground">Unique Guests</div>
          </div>
        </div>
        
        {data.weekly?.daily_breakdown && data.weekly.daily_breakdown.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Last 7 Days</h4>
            <SimpleBarChart 
              data={data.weekly.daily_breakdown}
              labelKey="date"
              valueKey="visits"
              color="#7C3AED"
            />
          </div>
        )}
        
        <div className="mt-4 text-center text-sm text-muted-foreground">
          Weekly Total: {data.weekly?.total_visits || 0} visits
        </div>
      </CardContent>
    </Card>
  );
};



// To-Do Analytics Card
export const TodoAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/todos');
      if (!error && result) setData(result);
    } catch (e) {
      console.error('Failed to fetch todo data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="todo-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-blue-500" />
          To-Do Analytics
        </CardTitle>
        <CardDescription>Task creation and completion stats</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{data.total_todos}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Total Tasks</div>
          </div>
          <div className="text-center p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 dark:text-green-400">{data.completed_todos}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Completed</div>
          </div>
          <div className="text-center p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{data.completion_rate}%</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Completion Rate</div>
          </div>
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 dark:text-purple-400">{data.adoption_rate}%</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">User Adoption</div>
          </div>
        </div>

        {data.top_users?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Top Users by Tasks</h4>
            <div className="space-y-1">
              {data.top_users.map((u, i) => (
                <div key={u.user_id || u.name || `tasks-user-${i}`} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="truncate flex-1">{u.name}</span>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{u.total} tasks</Badge>
                    <Badge variant="outline" className="text-green-600 dark:text-green-400">{u.completed} done</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {Object.keys(data.category_breakdown || {}).length > 0 && (
          <>
            <h4 className="text-sm font-medium mb-2">Tasks by Category</h4>
            <SimpleBarChart
              data={Object.entries(data.category_breakdown).map(([k, v]) => ({ name: k, count: v }))}
              labelKey="name"
              valueKey="count"
              color="#3B82F6"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Timer Analytics Card  
export const TimerAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: result, error } = await apiFetch('/api/admin/analytics/timer');
      if (!error && result) setData(result);
    } catch (e) {
      console.error('Failed to fetch timer data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Card><CardContent className="p-6"><p className="text-muted-foreground">Loading...</p></CardContent></Card>;
  if (!data) return null;

  return (
    <Card data-testid="timer-analytics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Target className="h-5 w-5 text-purple-500" />
          Timer Analytics
        </CardTitle>
        <CardDescription>Focus session usage and trends</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 dark:text-purple-400">{data.total_sessions}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Total Sessions</div>
          </div>
          <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 dark:text-indigo-400">{data.total_minutes}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Total Minutes</div>
          </div>
          <div className="text-center p-3 bg-violet-50 dark:bg-violet-950/30 rounded-lg">
            <div className="text-2xl font-bold text-violet-600 dark:text-violet-400 dark:text-violet-400">{data.avg_session_minutes}</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">Avg Min/Session</div>
          </div>
          <div className="text-center p-3 bg-pink-50 dark:bg-pink-950/30 rounded-lg">
            <div className="text-2xl font-bold text-pink-600 dark:text-pink-400">{data.adoption_rate}%</div>
            <div className="text-xs text-muted-foreground dark:text-muted-foreground">User Adoption</div>
          </div>
        </div>

        {data.daily_sessions?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Daily Sessions (14 days)</h4>
            <SimpleBarChart
              data={data.daily_sessions.slice(-7)}
              labelKey="date"
              valueKey="sessions"
              color="#8B5CF6"
            />
          </div>
        )}

        {data.top_users?.length > 0 && (
          <>
            <h4 className="text-sm font-medium mb-2">Top Users by Focus Time</h4>
            <div className="space-y-1">
              {data.top_users.map((u, i) => (
                <div key={u.user_id || u.name || `focus-user-${i}`} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="truncate flex-1">{u.name}</span>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{u.sessions} sessions</Badge>
                    <Badge variant="outline" className="text-purple-600 dark:text-purple-400">{u.total_minutes} min</Badge>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
